import { Storage } from '../lib/storage';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Edit3, Check, Loader2, ChevronUp, ChevronDown } from 'lucide-react';
import { supabase } from '../lib/supabase';

export default function ApodosModal({ onClose, themeClass }: { onClose: () => void, themeClass: string }) {
  const [cargando, setCargando] = useState(true);
  const [motos, setMotos] = useState<any[]>([]);
  const [userAlias, setUserAlias] = useState('');
  const [userRealName, setUserRealName] = useState('');
  const [guardadoId, setGuardadoId] = useState<string | null>(null);

  const formatTitleCase = (str: string) => {
    return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
  };

  useEffect(() => {
    const fetchInfo = async () => {
      let uid = Storage.get('gravity_user_id');
      if (!uid) {
         const { data: { session } } = await supabase.auth.getSession();
         uid = session?.user?.id || null;
      }
      if (!uid) return;

      const { data: ud } = await supabase.from('usuarios').select('alias, nombre').eq('id', uid).single();
      if (ud) {
        setUserAlias(ud.alias || '');
        setUserRealName(ud.nombre || '');
      }

      const { data: vehiculos } = await supabase.from('vehiculos').select('*').eq('usuario_id', uid);
      if (vehiculos) {
        setMotos(vehiculos);
      }
      setCargando(false);
    };
    fetchInfo();
  }, []);

  const handleSaveUserAlias = async () => {
    const uid = Storage.get('gravity_user_id');
    if (!uid) return;
    setGuardadoId('user-alias');
    
    // Save to DB
    await supabase.from('usuarios').update({ alias: userAlias }).eq('id', uid);
    
    // Update local cache
    const userStr = Storage.get('gravity_user');
    if (userStr && userStr.startsWith('{')) {
      try {
        const p = JSON.parse(userStr);
        p.alias = userAlias;
        Storage.set('gravity_user', JSON.stringify(p));
      } catch(e) {}
    }

    setTimeout(() => setGuardadoId(null), 2000);
    window.dispatchEvent(new CustomEvent('apodosActualizados'));
  };

  const moveMoto = (index: number, dir: 'up' | 'down') => {
    if ((dir === 'up' && index === 0) || (dir === 'down' && index === motos.length - 1)) return;
    const items = [...motos];
    const swapIdx = dir === 'up' ? index - 1 : index + 1;
    [items[index], items[swapIdx]] = [items[swapIdx], items[index]];
    setMotos(items);
    Storage.set('gravity_moto_order', JSON.stringify(items.map(m => m.id.toString())));
  };

  const handleSaveMotoApodo = async (idMoto: string, index: number) => {
    setGuardadoId(idMoto);
    const motoALocal = motos[index];
    await supabase.from('vehiculos').update({ apodo: motoALocal.apodo }).eq('id', idMoto);
    setTimeout(() => setGuardadoId(null), 2000);
    window.dispatchEvent(new CustomEvent('apodosActualizados'));
  };

  const updateMotoApodoLocal = (idx: number, newVal: string) => {
     const newM = [...motos];
     newM[idx].apodo = newVal;
     setMotos(newM);
  };

  return (
    <div className="absolute inset-0 z-[300] flex flex-col justify-end">
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div 
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`relative w-full ${themeClass} rounded-t-3xl p-6 shadow-[0_-10px_40px_rgba(0,168,255,0.2)] border-t border-primary-cyan/20 pb-10 max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-12 h-1 rounded-full bg-white/20 mx-auto mb-6"></div>
        <button onClick={onClose} className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors">
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-xl font-black mb-2 flex items-center gap-2 text-primary-cyan uppercase tracking-wide">
          <Edit3 className="w-6 h-6" /> Títulos Legales
        </h2>
        <p className="text-xs text-text-muted mb-6 leading-relaxed">
          Los apodos que establezcas aquí dominarán tu garaje y el perfil público. Si los dejas vacíos, se usará tu nombre real y el RUNT de cada moto.
        </p>

        {cargando ? (
           <div className="flex justify-center items-center py-10"><Loader2 className="w-8 h-8 animate-spin text-primary-cyan" /></div>
        ) : (
          <div className="flex flex-col gap-6">
            
            {/* Vehículos Reales */}
            {motos.length > 0 ? (
                <>
                  <h3 className="text-sm font-extrabold text-white mt-2 border-b border-white/10 pb-2">TUS VEHÍCULOS</h3>
                  <div className="flex flex-col gap-4">
                    {motos.map((moto, i) => (
                      <div key={moto.id} className="flex flex-col gap-1.5 focus-within:scale-[1.02] transition-transform">
                        <span className="text-[11px] text-white/80 font-bold ml-1 uppercase tracking-widest">{moto.marca} {moto.modelo} ({moto.placa})</span>
                        <div className="flex gap-2 items-center">
                          <div className="flex flex-col border border-white/5 rounded px-1 py-0.5 shrink-0 bg-white/5">
                            <button onClick={() => moveMoto(i, 'up')} className="text-slate-400 hover:text-primary-cyan active:scale-90"><ChevronUp className="w-4 h-4" /></button>
                            <button onClick={() => moveMoto(i, 'down')} className="text-slate-400 hover:text-primary-cyan active:scale-90"><ChevronDown className="w-4 h-4" /></button>
                          </div>
                          <input 
                            type="text" 
                            value={moto.apodo || ''}
                            onChange={(e) => updateMotoApodoLocal(i, formatTitleCase(e.target.value))}
                            className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 h-10 text-xs text-white font-bold outline-none focus:border-primary-cyan transition-colors placeholder:text-white/20 shadow-inner"
                            placeholder="Ej: La Bestia..."
                          />
                          <button 
                            onClick={() => handleSaveMotoApodo(moto.id, i)}
                            className={`h-10 w-12 rounded-xl font-bold flex items-center justify-center transition-all text-xs ${guardadoId === moto.id ? 'bg-green-500 text-white' : 'bg-slate-700 text-white hover:bg-slate-600 active:scale-95'}`}
                          >
                            {guardadoId === moto.id ? <Check className="w-4 h-4 mx-auto animate-bounce" /> : 'OK'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
            ) : (
                <p className="text-xs text-center text-slate-500 py-6">No tienes vehículos ingresados en la bóveda.</p>
            )}
          </div>
        )}

      </motion.div>
    </div>
  );
}
